import React, { useState, useEffect } from 'react';
import { Warning } from '@mui/icons-material';
import comms from './comms';

// EmergencyAlarm — driver panic button. A tap opens a confirm step (so it can't
// fire by accident), then sends a durable, location-tagged distress alert to
// dispatch via comms.sendEmergencyAlert (socket → HTTP → offline queue; never
// silently dropped). While active, the driver can stand down (I'm safe), which
// sends comms.cancelEmergencyAlert.
//
// Two modes:
//  • Overt    — a full red banner stays on screen until stood down.
//  • Discreet — sends the same alert but shows only a tiny dot (covert): useful
//               when the driver doesn't want a visible alarm in front of others.

// Best-effort current GPS (short timeout). Resolves {} if unavailable —
// sendEmergencyAlert still goes out with "location unavailable".
function getCoords() {
  return new Promise((resolve) => {
    if (typeof navigator === 'undefined' || !navigator.geolocation) return resolve({});
    navigator.geolocation.getCurrentPosition(
      (pos) => resolve({ latitude: pos.coords.latitude, longitude: pos.coords.longitude }),
      () => resolve({}),
      { enableHighAccuracy: true, timeout: 5000, maximumAge: 15000 }
    );
  });
}

function EmergencyAlarm({ showFloatingButton = true }) {
  const [confirming, setConfirming] = useState(false);
  const [active, setActive] = useState(false); // overt alarm on screen
  const [discreetActive, setDiscreetActive] = useState(false); // covert
  const [sending, setSending] = useState(false);
  const [error, setError] = useState('');

  // Allow opening the confirm step from elsewhere (e.g. the sidebar "Emergency SOS"
  // menu item) by dispatching a global 'driverapp:open-sos' event.
  useEffect(() => {
    const openFromMenu = () => {
      setError('');
      setConfirming(true);
    };
    window.addEventListener('driverapp:open-sos', openFromMenu);
    return () => window.removeEventListener('driverapp:open-sos', openFromMenu);
  }, []);

  const trigger = async (discreet) => {
    setSending(true);
    setError('');
    try {
      const coords = await getCoords();
      const res = await comms.sendEmergencyAlert(coords);
      const ok = res && (res.sent || res.queued || res.status === true || res.success === true);
      if (ok) {
        setConfirming(false);
        if (discreet) setDiscreetActive(true);
        else setActive(true);
      } else {
        setError('Could not send the alert. Try again, or call 911.');
      }
    } catch (e) {
      setError('Could not send the alert. Try again, or call 911.');
    } finally {
      setSending(false);
    }
  };

  const standDown = async () => {
    setSending(true);
    try {
      await comms.cancelEmergencyAlert();
    } catch (e) {
      /* stand-down is best-effort */
    }
    setActive(false);
    setDiscreetActive(false);
    setSending(false);
  };

  return (
    <>
      {/* Active overt banner — sits above the header during an emergency */}
      {active && (
        <div className="fixed inset-x-0 top-0 z-[3000] flex items-center justify-between gap-2 bg-red-600 px-4 py-2 text-white shadow-lg">
          <span className="flex items-center gap-2 text-sm font-bold">
            <Warning style={{ fontSize: 18 }} /> Emergency alert sent — help requested
          </span>
          <button
            onClick={standDown}
            disabled={sending}
            className="shrink-0 rounded-lg bg-white/20 px-3 py-1 text-xs font-semibold hover:bg-white/30 disabled:opacity-60"
          >
            {sending ? '…' : "I'm safe"}
          </button>
        </div>
      )}

      {/* Discreet active — tiny dot only; tap to stand down */}
      {discreetActive && !active && (
        <button
          onClick={standDown}
          aria-label="Emergency active (discreet) — tap to stand down"
          title="Emergency active (discreet) — tap to stand down"
          className="fixed bottom-6 left-6 z-[3000] h-4 w-4 rounded-full bg-red-600 shadow"
        />
      )}

      {/* Floating SOS trigger (optional; hidden while an alert is active) */}
      {showFloatingButton && !active && !discreetActive && (
        <button
          onClick={() => {
            setError('');
            setConfirming(true);
          }}
          aria-label="Emergency SOS"
          className="fixed bottom-6 left-6 z-[2000] flex h-14 w-14 items-center justify-center rounded-full bg-red-600 text-sm font-extrabold tracking-wide text-white shadow-lg transition-transform active:scale-90"
        >
          SOS
        </button>
      )}

      {/* Confirm step */}
      {confirming && (
        <div
          className="fixed inset-0 z-[3100] flex items-end justify-center bg-black/50 sm:items-center"
          onClick={() => !sending && setConfirming(false)}
        >
          <div
            className="w-full max-w-md rounded-t-2xl bg-white p-5 shadow-xl sm:rounded-2xl"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="flex items-center gap-2 text-red-600">
              <Warning />
              <span className="text-lg font-bold">Send emergency alert?</span>
            </div>
            <p className="mt-2 text-sm text-gray-600">
              Dispatch is alerted immediately with your live location and asked to send help.
              Use only in a real emergency.
            </p>

            {error && (
              <div className="mt-3 rounded-lg bg-red-50 px-3 py-2 text-sm text-red-700">{error}</div>
            )}

            <button
              onClick={() => trigger(false)}
              disabled={sending}
              className="mt-4 w-full rounded-xl bg-red-600 px-4 py-3 text-base font-bold text-white transition-transform active:scale-95 disabled:opacity-60"
            >
              {sending ? 'Sending…' : 'Send emergency alert'}
            </button>
            <button
              onClick={() => trigger(true)}
              disabled={sending}
              className="mt-2 w-full rounded-xl border border-red-300 px-4 py-2.5 text-sm font-semibold text-red-700 disabled:opacity-60"
            >
              Send discreetly (no on-screen alarm)
            </button>
            <button
              onClick={() => setConfirming(false)}
              disabled={sending}
              className="mt-2 w-full rounded-xl bg-gray-100 px-4 py-2.5 text-sm font-semibold text-gray-700 disabled:opacity-60"
            >
              Cancel
            </button>
          </div>
        </div>
      )}
    </>
  );
}

export default EmergencyAlarm;
